import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../../components";
import { useNavigate } from "react-router-dom";
import ApiService from "../../../service/ApiService";
import AlertService from "../../../service/AlertService";
import DatabaseEntities from "../../../model/enum/DatabaseEntities";
import FirebaseService from "../../../service/FirebaseService";
import DeleteModal from "../../modal/DeleteModal";
import Bundle from "../../../model/bundle";
import Product from "../../../model/product";
import ToastMessages from "../../../model/enum/ToastMessages";
import EditIcon from "../../../assets/admin/edit-icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "../../../assets/admin/upload-icon.svg";
import PageResultResponse from "../../../model/pageResultResponse";
import Select, { StylesConfig } from "react-select";
import Misc from "../../../utils/Misc";
import Pathnames from "../../../model/enum/Pathnames";
import "../../styles/tableStyles.css";
import useStyles from "./styles";
import {
  DarkGrey,
  LightPrimaryColor,
  PrimaryColor,
  White,
} from "../../../shared/styles/colors";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

const styles: StylesConfig<any, true> = {
  container: (base) => ({
    ...base,
    width: "95% !important",
    borderColor: DarkGrey,
    borderRadius: "5px",
    marginLeft: "10px",
  }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? PrimaryColor : DarkGrey,
    boxShadow: state.isFocused ? LightPrimaryColor : base.boxShadow,
    "&:hover": {
      borderColor: PrimaryColor,
    },
    "&:selected": {
      borderColor: PrimaryColor,
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? LightPrimaryColor
      : state.isSelected
      ? PrimaryColor
      : base.backgroundColor,
    color: state.isFocused || state.isSelected ? White : base.color,
  }),
  menu: (base) => ({
    ...base,
  }),
};

type Item = {
  id: number;
  bundleId?: number | null | "";
  productId?: number | null | "";
  quantity: number;
};

const UltraBundleModal = ({ ultrabundle }: any) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const navigate = useNavigate();

  const edit = ultrabundle != null;
  const [name, setName] = useState(edit ? ultrabundle.name : null);
  const [description, setDescription] = useState(
    edit ? ultrabundle.description : null
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [items, setItems] = useState<any[]>(
    edit ? ultrabundle.BundleItems : []
  );
  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileName, setFileName] = useState("");
  const [imageUrl, setImageUrl] = useState(edit ? ultrabundle.imageUrl : null);
  const [loading, setLoading] = useState(true);

  // //Get all the products from the database
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [fetchProducts, fetchBundles] = await Promise.all([
          ApiService.get<PageResultResponse>(DatabaseEntities.PRODUCTS),
          ApiService.get<PageResultResponse>(DatabaseEntities.BUNDLES),
        ]);
        setProducts(fetchProducts.items);
        setBundles(fetchBundles.items);
      } catch (error) {
        AlertService.error(ToastMessages.FETCH_FAILED);
      } finally {
        setLoading(false);
      }
    };
    if (loading && Misc.isArrayEmpty(products) && Misc.isArrayEmpty(bundles)) {
      fetchData();
    }
  }, [products, bundles]);

  const handleName = (e: any) => setName(e.target.value);
  const handleDescription = (e: any) => setDescription(e.target.value);

  const handleFileChange = (e: any) => {
    setFile(e.target.files[0]);
    setFileName(e.target.files[0].name);
  };

  const handleItem = (e: any, index: number): void => {
    const values = [...items];
    if (e) {
      if (
        e.label.includes("Bundle:") &&
        bundles.find((b) => b.id === e.value)
      ) {
        values[index].bundleId = Number(e.value);
        values[index].productId = null;
      } else if (
        e.label.includes("Product:") &&
        products.find((p) => p.id === e.value)
      ) {
        values[index].productId = Number(e.value);
        values[index].bundleId = null;
      }
    } else {
      values[index].bundleId = null;
      values[index].productId = null;
    }
    setItems(values);
  };

  const handleQuantity = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ): void => {
    const values = [...items];
    values[index].quantity = parseInt(e.target.value);
    setItems(values);
  };

  const handleDeleteItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleAddItem = (): void => {
    const newItem: Item = {
      id: items.length + 1,
      bundleId: null, // Adjusting type for bundleService
      productId: null, // Adjusting type for productService
      quantity: 0,
    };

    setItems([...items, newItem]);
  };

  const resetList = () => {
    setTimeout(() => {
      navigate(Pathnames.PRODUCTS_SERVICES_BUNDLE_LIST);
    }, 3000);
  };

  const handleCreate = async (payload: Bundle) => {
    if (file) {
      const uploadResult = await FirebaseService.uploadFile(
        file,
        fileName,
        "UltraBundle"
      );
      payload.imageUrl = uploadResult;
    }

    await ApiService.post<Bundle>(DatabaseEntities.ULTRA_BUNDLES, payload)
      .then(() => {
        AlertService.success(ToastMessages.CREATED);
        resetList();
        setName(null);
        setDescription(null);
        setFileName("");
        setFile(undefined);
        setImageUrl(null);
        setItems([]);
      })
      .catch((error: any) => {
        console.error("Error creating bundle:", error);
        AlertService.error(`${ToastMessages.CREATE_FAILED}: ${error.message}`);
      });
  };

  const handleUpdate = async (payload: Bundle) => {
    await ApiService.put<Bundle>(
      `${DatabaseEntities.ULTRA_BUNDLES}/${payload.id}`,
      payload
    )
      .then(() => {
        AlertService.success(ToastMessages.UPDATED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: Bundle) => {
    await ApiService.delete<any>(
      `${DatabaseEntities.ULTRA_BUNDLES}/${payload.id}`
    )
      .then(() => {
        AlertService.success(ToastMessages.DELETED);
        resetList();
        handleClose();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const invalidInput = (): boolean => {
    return Misc.isNullOrEmpty(name) || items?.length === 0;
  };

  const submit = async () => {
    if (invalidInput()) return;

    setLoading(true);

    let payload: Bundle = {
      id: edit ? ultrabundle.id : 0,
      name: name,
      description: description,
      imageUrl: imageUrl,
      items: items,
      isUltra: true,
    };

    if (edit) await handleUpdate(payload);
    else await handleCreate(payload);

    setLoading(false);
    handleClose();
  };

  return (
    <div>
      {edit ? (
        <Button onClick={handleOpen}>
          <img src={EditIcon} alt="edit icon" />
        </Button>
      ) : (
        <div style={{ cursor: "pointer" }} onClick={handleOpen}>
          <Typography className={classes.subTitle}>Ultra-Bundle</Typography>
          <Typography>
            This modal allows you to create an ultra bundle by combining
            multiple bundles, products, or services into a single package for
            streamlined management and availability.
          </Typography>
        </div>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Box sx={style}>
            <div className={classes.headerDiv}>
              {edit ? (
                <Typography className={classes.title}>
                  <DeleteModal item={ultrabundle} handleDelete={handleDelete} />
                </Typography>
              ) : (
                <Typography className={classes.title}>
                  Create ultra-bundle
                </Typography>
              )}
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
            {edit && <hr />}
            <div className={classes.form}>
              <Typography className={classes.label}>
                Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <input
                className={classes.input}
                value={name}
                onChange={handleName}
                placeholder="Add text..."
              />
              <Typography className={classes.label}>
                Description <span style={{ color: "red" }}>*</span>
              </Typography>
              <textarea
                className={classes.input}
                value={description}
                onChange={handleDescription}
                placeholder="Add a description.."
              />
              {!edit && (
                <>
                  <div className={classes.inputContainer}>
                    <Typography className={classes.label}>Image</Typography>
                  </div>
                  <div className={classes.uploadInput}>
                    <input
                      type="file"
                      id="add-single-img"
                      accept="image/*"
                      className="d-none"
                      onChange={handleFileChange}
                    />
                    <label id="image-label" htmlFor="add-single-img">
                      <img
                        src={UploadIcon}
                        className={classes.image}
                        alt="upload icon"
                      />
                      <label>Drop files to upload or browse</label>
                      <Typography>{fileName}</Typography>
                    </label>
                  </div>
                </>
              )}
              <Typography style={{ marginTop: "20px", marginBottom: "10px" }}>
                Bundles/Products included into the Ultra Bundle{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <div className="table-container">
                <table className="styled-table">
                  <thead>
                    <td>Bundle/Product</td>
                    <td>Quantity</td>
                    <td>Action</td>
                  </thead>
                  <tbody>
                    {items?.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          <Select
                            styles={styles}
                            options={[
                              ...bundles.map((bundle) => ({
                                value: bundle.id,
                                label: `Bundle: ${bundle.name}`,
                              })),
                              ...products.map((product) => ({
                                value: product.id,
                                label: `Product: ${product.name}`,
                              })),
                            ]}
                            onChange={(e) => handleItem(e, index)}
                            value={
                              item.productId
                                ? {
                                    value: item.productId,
                                    label:
                                      products.find(
                                        (p) => p.id === item.productId
                                      )?.name || "",
                                  }
                                : item.bundleId
                                ? {
                                    value: item.bundleId,
                                    label:
                                      bundles.find(
                                        (b) => b.id === item.bundleId
                                      )?.name || "",
                                  }
                                : null
                            }
                            placeholder="Select bundle/product"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            placeholder="01"
                            min={1}
                            max={99}
                            value={items[index].quantity}
                            onChange={(e) => handleQuantity(e, index)}
                          />
                        </td>
                        <td>
                          <Button
                            className="delete-btn"
                            onClick={() => handleDeleteItem(index)}
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>
                    ))}
                    <tr className="add-item-row">
                      <td
                        colSpan={3}
                        className="add-item"
                        onClick={handleAddItem}
                      >
                        <span>+ Add Item</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className={classes.buttonDiv}>
                <Btn
                  className={classes.button}
                  onClick={submit}
                  disabled={invalidInput()}
                >
                  {edit ? "Save" : "Create"}
                </Btn>
              </div>
            </div>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default UltraBundleModal;
